import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import { Flower } from "@/components/site/Flower";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import { SectionCta } from "@/components/site/SectionCta";
  import { TelehealthCta } from "@/components/site/TelehealthCta";
  import { LazyTurnstile } from "@/components/site/LazyTurnstile";
  import { sendSelfPayInquiry } from "@/lib/inquiries.functions";
import { REVISION_PRICE } from "@/lib/self-pay-revision-price";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

const SITE = "https://virginiabariatric.com";
const PAGE_URL = `${SITE}/self-pay-bariatric-surgery`;
const PAGE_TITLE = "Self-Pay Bariatric Surgery Cost | Northern Virginia";
const PAGE_DESCRIPTION =
  "See current self-pay prices for gastric sleeve, bypass, Lap-Band, and revision surgery in Northern Virginia. What is included, payment plans, and next steps.";

type PriceRow = {
  procedure: string;
  to?: string;
  anchor?: string;
  facility: string;
  anesthesia: string;
  physician: string;
  total: string;
};

const SURGICAL: PriceRow[] = [
  { procedure: "Gastric Bypass", to: "/services/bypass", anchor: "gastric-bypass-cost", facility: "$14,000 (1 day)", anesthesia: "$1,400*", physician: "$7,800", total: "$23,200*" },
  { procedure: "Gastric Sleeve", to: "/services/sleeve", anchor: "gastric-sleeve-cost", facility: "$10,000 (1 day)", anesthesia: "$1,400*", physician: "$7,800", total: "$19,200*" },
  { procedure: "Lap-Band", to: "/services/band", anchor: "lap-band-cost", facility: "$10,000 (1 day)", anesthesia: "$1,400*", physician: "$7,800", total: "$19,200*" },
  REVISION_PRICE,
];

const ENDOSCOPIC: PriceRow[] = [
  { procedure: "ESG (Endoscopic Sleeve Gastroplasty)", to: "/services/esg", anchor: "esg-cost", facility: "$9,000*", anesthesia: "$1,400*", physician: "$2,500", total: "$12,900" },
  { procedure: "TORe (Transoral Outlet Reduction)", facility: "$8,000*", anesthesia: "$1,400*", physician: "$2,500", total: "$11,900" },
  { procedure: "Orbera Gastric Balloon", to: "/services/balloon", anchor: "orbera-balloon-cost", facility: "$6,000*", anesthesia: "$1,400*", physician: "$1,500", total: "$8,900" },
];


const FAQS: Faq[] = [
  {
    q: "Why would anyone self-pay if they have insurance?",
    schemaAnswer:
      "The waiting period is a common reason. Insurance-covered bariatric surgery frequently requires as much as twelve months of supervised diet visits. The pre-authorization process is sometimes a slog, and occasionally an insurer will deny prior authorization or require documentation that is impossible to get. Patients also choose self-pay because their plan excludes bariatric surgery entirely, or because their deductible and out-of-pocket maximum would cost nearly as much as the self-pay price. Some simply do not want to wait.",
    a: (
      <>
        <p>
          The waiting period is a common reason. Insurance-covered bariatric surgery frequently requires as much as twelve months of supervised diet visits.
        </p>
        <p className="mt-2">
          The pre-authorization process is sometimes a slog. It&rsquo;s usually straightforward, but occasionally an insurer will turn it into a true bureaucratic nightmare. They may require documentation that is simply impossible to get, or they deny prior authorization for an absurd reason. Sometimes the state insurance ombudsman can help. Many times, they can&rsquo;t. Self-pay can get you out of those headaches.
        </p>
        <p className="mt-2">
          Patients also choose self-pay because their plan excludes bariatric surgery entirely, or because their deductible and out-of-pocket maximum would cost nearly as much as the self-pay price. Some simply don&rsquo;t want to wait.
        </p>
      </>
    ),
  },
  {
    q: "Is the self-pay price really what I will pay?",
    schemaAnswer:
      "Yes, or at least it should be quite close. The quoted total covers the surgeon fee, one hospital day, and an anesthesia estimate. Your consultation, psychological evaluation, medical clearance, labs, and EKG are billed separately and are usually covered by insurance. Additional hospital days are $1,000 each. We go over every line with you before you commit.",
    a: (
      <p>
        Yes, or at least it should be quite close. The quoted total covers the surgeon fee, one hospital day, and an anesthesia estimate. Your consultation, psychological evaluation, medical clearance, labs, and EKG are billed separately. If you have insurance, it will usually cover those things. Additional hospital days are $1,000 each. We will go over every line with you before you commit. We work hard to make sure there are no surprises.
      </p>
    ),
  },
  {
    q: "What happens if I need to stay in the hospital longer than one day?",
    schemaAnswer:
      "Each additional day at INOVA Fair Oaks Hospital is $1,000, billed by the hospital. About 90% of sleeve and bypass patients go home the day after surgery, but recovery is individual and Dr. Fitzer will keep you as long as is medically appropriate.",
    a: (
      <p>
        Each additional day at INOVA Fair Oaks Hospital is $1,000, billed by the hospital. 90% of sleeve and bypass patients go home the day after surgery, but your recovery is individual and Dr. Fitzer will keep you as long as is medically appropriate. (Like Ernest and Julio Gallo, Dr. Fitzer will sell no wine before its time!)
      </p>
    ),
  },
  {
    q: "I already had bariatric surgery elsewhere. Can I be a self-pay revision patient?",
    schemaAnswer:
      `Yes. Dr. Fitzer is highly experienced with revision surgery. Revision surgery has a surgeon fee of ${REVISION_PRICE.physician} and an estimated total of ${REVISION_PRICE.total.replace(/\*$/, "")}.`,
    a: (
      <p>
        Of course. Dr. Fitzer is highly experienced with{" "}
        <Link to="/services/revisions" className="text-brand-mid font-medium hover:underline">
          revision surgery
        </Link>
        . Revision surgery has a surgeon fee of {REVISION_PRICE.physician} and an estimated total of {REVISION_PRICE.total.replace(/\*$/, "")}.
      </p>
    ),
  },
  {
    q: "What is the difference between the endoscopic procedures and surgery?",
    schemaAnswer:
      "ESG, TORe, and the Orbera balloon are done entirely through the mouth with a flexible endoscope, with no incisions and no overnight stay. ESG reshapes the stomach with internal sutures, TORe tightens a stretched connection after a previous gastric bypass, and Orbera places a temporary saline-filled balloon that is removed after about six months. They are generally lower-risk and faster to recover from than surgery.",
    a: (
      <p>
        ESG, TORe, and the Orbera balloon are done entirely through the mouth with a flexible endoscope &mdash; no incisions, no cutting or stapling of the stomach from the outside, and no overnight stay. ESG reshapes the stomach with internal sutures. TORe tightens a stretched connection after a previous gastric bypass. Orbera places a temporary saline-filled balloon in the stomach, which is removed after about six months. They are generally lower-risk and faster to recover from than surgery, and are reversible or non-permanent in ways surgery is not. Dr. Fitzer will talk through which option fits your anatomy, your goals, and your history at your consultation.
      </p>
    ),
  },
  {
    q: "I had a gastric bypass years ago and have regained weight. What are my options?",
    schemaAnswer:
      "You may be a candidate for TORe, an endoscopic procedure that tightens the stretched outlet between the stomach pouch and the small intestine. Surgical revision is another option. Dr. Fitzer will want your original operative report and recent imaging or endoscopy results before recommending one over the other.",
    a: (
      <p>
        You may be a candidate for TORe, an endoscopic procedure that tightens the stretched outlet between the stomach pouch and the small intestine &mdash; a common reason weight comes back after bypass. Surgical revision is another option. Dr. Fitzer will want your original operative report and recent imaging or endoscopy results before recommending one over the other.
      </p>
    ),
  },
  {
    q: "Do I need a BMI of 35 or 40 to qualify?",
    schemaAnswer:
      "Not for self-pay. The self-pay requirement is a BMI of 30 or higher. Insurance carriers use higher BMI thresholds, which is another reason patients with a BMI in the low 30s choose to self-pay.",
    a: (
      <p>
        Not for self-pay. The self-pay requirement is a BMI of 30 or higher. Insurance carriers use BMI thresholds that are above the recommended ones. It is another reason patients with a BMI in the low 30s choose to self-pay.
      </p>
    ),
  },
  {
    q: "What if I start the process and then change my mind?",
    schemaAnswer:
      "You are not locked in. If you complete diet visits and then decide not to proceed, those visits are billed at $85 each. Payments toward the surgeon fee are handled according to the office surgery cancelation policy.",
    a: (
      <p>
        Maybe something important came up. Maybe you simply had second thoughts. We get it. You are not locked in. If you complete diet visits and then decide not to proceed, those visits are billed at $85 each. Payments toward the surgeon fee are handled according to the office surgery cancelation policy.
      </p>
    ),
  },
  {
    q: "What is your surgery cancellation policy?",
    schemaAnswer:
      "Cancel as early as possible. If you cancel within fourteen days of the surgery date, there is a $500 fee. Prior to fourteen days, there is no cancelation fee.",
    a: (
      <>
        <p>
          Try to cancel as early as possible. If you cancel within fourteen days of the surgery date, there is a $500 fee. Prior to fourteen days, there is no cancelation fee.
        </p>
        <p className="mt-2">
          Stuff comes up in life. You can count on us to be understanding about it. Just talk to us as soon as you know, and we will work it out together.
        </p>
      </>
    ),
  },
  {
    q: "Is my consultation included in the self-pay price?",
    schemaAnswer:
      "Yes, if you proceed with surgery. If you opt not to proceed, we bill your consultation to your insurance first. If it is not covered, the consultation is billed to you at $333.75.",
    a: (
      <p>
        Yes, if you proceed with surgery. If you opt not to proceed, we bill your consultation to your insurance first. If it is not covered, the consultation is billed to you at $333.75.
      </p>
    ),
  },
  {
    q: "Can I use my HSA or FSA?",
    schemaAnswer:
      "Yes. Bariatric surgery is almost always a qualified medical expense. Check with your plan administrator and keep your receipts and any letter of medical necessity we provide.",
    a: (
      <p>
        Yes. Bariatric surgery is almost always a qualified medical expense. Check with your plan administrator and keep your receipts and any letter of medical necessity we provide.
      </p>
    ),
  },
  {
    q: "I live outside Northern Virginia. Can I still come to you?",
    schemaAnswer:
      "Yes. We see patients from all over the world. Tell our office where you are traveling from and we will help you minimize the number of trips. Most post-operative follow-up can be done via telehealth, and we can coordinate with a provider local to you for routine visits.",
    a: (
      <p>
        We see patients from all over the world. Tell our office where you are traveling from, and we will help you minimize the number of trips you need to make. Most post-operative follow-up can be done via telehealth. We can also coordinate with a provider who is local to you for routine visits.
      </p>
    ),
  },
  {
    q: "How soon could I have surgery?",
    schemaAnswer:
      "That depends mostly on how quickly you complete your clearances and the diet program. Self-pay patients do not wait on insurance authorization, so the timeline is generally driven by your own scheduling needs.",
    a: (
      <p>
        That depends mostly on how quickly you complete your clearances and the diet program. Self-pay patients do not wait on insurance authorization, so the timeline is generally driven by your own scheduling needs rather than a carrier&rsquo;s chosen policy.
      </p>
    ),
  },
];

export const Route = createFileRoute("/self-pay-bariatric-surgery")({
  head: () => ({
    meta: [
      { title: `${PAGE_TITLE} | Virginia Bariatric` },
      { name: "description", content: PAGE_DESCRIPTION },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ...(import.meta.env.SSR ? [
        { property: "og:site_name", content: "Virginia Bariatric Surgery" },
        { property: "og:locale", content: "en_US" },
        { property: "og:title", content: `${PAGE_TITLE} | Virginia Bariatric` },
        { property: "og:description", content: PAGE_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: PAGE_URL },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${PAGE_TITLE} | Virginia Bariatric` },
        { name: "twitter:description", content: PAGE_DESCRIPTION },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: canonicalAndAlternates("/self-pay-bariatric-surgery"),
    scripts: import.meta.env.SSR
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "MedicalWebPage",
              "@id": `${PAGE_URL}#webpage`,
              url: PAGE_URL,
              name: PAGE_TITLE,
              headline: PAGE_TITLE,
              description: PAGE_DESCRIPTION,
              inLanguage: "en-US",
              lastReviewed: "2026-09-06",
              reviewedBy: PHYSICIAN_REF,
              isPartOf: { "@id": `${SITE}/#website` },
              publisher: { "@id": `${SITE}/#organization` },
              about: { "@type": "MedicalCondition", name: "Obesity" },
              audience: {
                "@type": "MedicalAudience",
                audienceType: "Self-pay bariatric surgery patients",
              },
              specialty: "Bariatric",
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify(faqSchema(FAQS, PAGE_URL)),
          },
        ]
      : [],
  }),
  component: SelfPayPage,
});

function PriceTable({ caption, rows }: { caption: string; rows: PriceRow[] }) {
  return (
    <div className="mt-6">
      <h3 className="font-serif text-lg text-brand-deep">{caption}</h3>


      {/* Desktop / tablet table */}
      <div className="mt-3 hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="bg-brand-soft text-left text-brand-deep">
              <th scope="col" className="border border-border px-3 py-2">Procedure</th>
              <th scope="col" className="border border-border px-3 py-2">Facility Fee</th>
              <th scope="col" className="border border-border px-3 py-2">Anesthesia*</th>
              <th scope="col" className="border border-border px-3 py-2">Physician Fee</th>
              <th scope="col" className="border border-border px-3 py-2">Estimated Total*</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.procedure} className="align-top">
                <th scope="row" className="border border-border px-3 py-2 text-left font-semibold">
                  {r.to ? (
                    <Link to={r.to} className="text-brand-mid hover:underline">{r.procedure}</Link>
                  ) : (
                    r.procedure
                  )}
                </th>
                <td className="border border-border px-3 py-2">{r.facility}</td>
                <td className="border border-border px-3 py-2">{r.anesthesia}</td>
                <td className="border border-border px-3 py-2">{r.physician}</td>
                <td className="border border-border px-3 py-2 font-semibold">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="mt-3 space-y-3 md:hidden">
        {rows.map((r) => (
          <li key={r.procedure} className="rounded-2xl border border-border bg-card p-4">
            <p className="font-semibold text-brand-mid">
              {r.to ? <Link to={r.to} className="hover:underline">{r.procedure}</Link> : r.procedure}
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Facility fee</dt>
              <dd>{r.facility}</dd>
              <dt className="text-muted-foreground">Anesthesia*</dt>
              <dd>{r.anesthesia}</dd>
              <dt className="text-muted-foreground">Physician fee</dt>
              <dd>{r.physician}</dd>
              <dt className="text-muted-foreground">Estimated total*</dt>
              <dd className="font-semibold">{r.total}</dd>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SelfPayPage() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitSelfPayInquiry = useServerFn(sendSelfPayInquiry);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security check before sending.");
      return;
    }

    setSending(true);
    setError(null);
    const formData = new FormData(event.currentTarget);

    try {
      await submitSelfPayInquiry({
        data: {
          name: String(formData.get("name") || "").trim(),
          phone: String(formData.get("phone") || "").trim() || undefined,
          email: String(formData.get("email") || "").trim(),
          procedure: String(formData.get("procedure") || "").trim() || undefined,
          message: String(formData.get("message") || "").trim(),
          turnstileToken,
        },
      });
      setSent(true);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageSection className="relative pt-6 md:pt-8 pb-3">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="font-serif text-4xl md:text-5xl">
            Self-Pay Bariatric Surgery in Northern Virginia
          </h1>
          <p className="mt-5 text-foreground/80 leading-[1.6] md:text-lg">
            If you don't have weight loss surgery coverage or want to bypass a long approval process, self-pay is an option. Self-pay offers a set price, simple requirements, and greater control of your schedule.
          </p>
          <TelehealthCta />
        </div>
      </PageSection>

      <PageSection className="py-3" maxWidth="4xl">
        <div className="space-y-4 text-foreground/80 leading-[1.6]">
          <p>
            Dr. Fitzer offers both surgical and endoscopic weight loss options:{" "}
            <Link to="/services/sleeve" className="text-brand-mid font-medium hover:underline">gastric sleeve</Link>,{" "}
            <Link to="/services/bypass" className="text-brand-mid font-medium hover:underline">gastric bypass</Link>,{" "}
            <Link to="/services/band" className="text-brand-mid font-medium hover:underline">Lap-Band</Link>, and{" "}
            <Link to="/services/revisions" className="text-brand-mid font-medium hover:underline">revision surgery</Link>, plus{" "}
            <Link to="/services/esg" className="text-brand-mid font-medium hover:underline">endoscopic sleeve gastroplasty (ESG)</Link>, transoral outlet reduction (TORe), and the{" "}
            <Link to="/services/balloon" className="text-brand-mid font-medium hover:underline">Orbera gastric balloon</Link>. Everything is performed at INOVA Fair Oaks Hospital, a full-service hospital with the anesthesia, nursing, and post-procedure care that bariatric patients should expect. We publish our self-pay pricing openly because we think you deserve to know the number before you invest time in the process.
          </p>
        </div>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">What Weight-Loss Surgery Costs Without Insurance</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          Surgical prices cover a one-day hospital stay and include the physician fee, hospital facility fee, and an anesthesia estimate. Endoscopic procedures are performed at INOVA Fair Oaks Hospital as outpatient procedures &mdash; you go home the same day.
        </p>

        <PriceTable caption="Surgical procedures — includes one hospital day" rows={SURGICAL} />
        <PriceTable caption="Endoscopic procedures — outpatient, same-day discharge" rows={ENDOSCOPIC} />

        <p className="mt-4 text-sm text-muted-foreground leading-[1.6]">
          * Estimate. Anesthesia is billed by an independent anesthesia group, so that portion is an estimate rather than a fixed fee. Totals that include it are therefore also estimates.
        </p>

        <SectionCta
          to="/contact"
          search={{ topic: "self-pay-callback" }}
          label="cta_self_pay_after_estimate"
        >
          Request Callback from Val or Jamie
        </SectionCta>

        <section className="mt-8 space-y-6 text-foreground/80 leading-[1.6]" aria-label="Costs by procedure">
          <section>
            <h3 id="gastric-sleeve-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">Gastric Sleeve Cost</h3>
            <p className="mt-2">
              The estimated total for gastric sleeve surgery is $19,200. That includes a $10,000 hospital facility fee for one day, an estimated $1,400 anesthesia fee, and a $7,800 physician fee. Gastric sleeve is a surgical procedure that reduces the size of your stomach.
            </p>
          </section>

          <section>
            <h3 id="gastric-bypass-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">Gastric Bypass Cost</h3>
            <p className="mt-2">
              The estimated total for gastric bypass surgery is $23,200. That includes a $14,000 hospital facility fee for one day, an estimated $1,400 anesthesia fee, and a $7,800 physician fee. Gastric bypass creates a small stomach pouch and changes how food travels through part of your digestive system.
            </p>
          </section>

          <section>
            <h3 id="lap-band-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">Lap-Band Cost</h3>
            <p className="mt-2">
              The estimated total for Lap-Band surgery is $19,200. That includes a $10,000 hospital facility fee for one day, an estimated $1,400 anesthesia fee, and a $7,800 physician fee. The procedure places an adjustable band around the upper part of your stomach.
            </p>
          </section>

          <section>
            <h3 id="revision-surgery-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">Revision Surgery Cost</h3>
            <p className="mt-2">
              The estimated total for revision surgery is {REVISION_PRICE.total.replace(/\*$/, "")}. That includes a {REVISION_PRICE.facility.replace(" (1 day)", "")} hospital facility fee for one day, an estimated {REVISION_PRICE.anesthesia.replace(/\*$/, "")} anesthesia fee, and a {REVISION_PRICE.physician} physician fee. The procedure is tailored to your previous operation, current anatomy, and reason for seeking a revision.
            </p>
          </section>

          <section>
            <h3 id="esg-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">ESG Cost</h3>
            <p className="mt-2">
              The total for endoscopic sleeve gastroplasty is $12,900. That includes a $9,000 facility fee, an estimated $1,400 anesthesia fee, and a $2,500 physician fee. ESG reshapes your stomach with internal sutures placed through the mouth, with no incisions or overnight stay.
            </p>
          </section>

          <section>
            <h3 id="tore-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">TORe Cost</h3>
            <p className="mt-2">
              The total for transoral outlet reduction is $11,900. That includes an $8,000 facility fee, an estimated $1,400 anesthesia fee, and a $2,500 physician fee. TORe is performed through the mouth to tighten a stretched connection after a previous gastric bypass, with no incisions or overnight stay.
            </p>
          </section>

          <section>
            <h3 id="orbera-balloon-cost" className="scroll-mt-28 font-serif text-lg text-brand-deep">Orbera Balloon Cost</h3>
            <p className="mt-2">
              The total for the Orbera gastric balloon is $8,900. That includes a $6,000 facility fee, an estimated $1,400 anesthesia fee, and a $1,500 physician fee. Orbera is a temporary saline-filled balloon placed through the mouth and removed after about six months, with no incisions or overnight stay.
            </p>
          </section>
        </section>

        <div className="mt-4 space-y-3 text-foreground/80 leading-[1.6]">
          <p>
            <strong>Endoscopic procedures:</strong> ESG, TORe, and the Orbera balloon are performed through the mouth with no incisions and no overnight stay.
          </p>
          <p>
            <strong>Additional hospital days:</strong> Each additional day at INOVA Fair Oaks Hospital is $1,000 per day.
          </p>
          <p>
            <strong>Prices shown are current self-pay rates and are subject to change.</strong> Call{" "}
            <a href="tel:7037099771" className="text-brand-mid font-medium hover:underline">(703) 709-9771</a>{" "}
            to confirm your quote before scheduling.
          </p>
          <p className="text-sm text-muted-foreground">Pricing last reviewed September 2026.</p>
        </div>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">What Your Self-Pay Price Includes</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          The surgeon fee portion of your self-pay price covers more than the operation itself:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-6 text-foreground/80 leading-[1.6]">
          <li>The surgical procedure performed by Dr. Fitzer</li>
          <li>The 3 dietitian sessions required before the procedure</li>
          <li>Your pre-operative appointments with our surgical team</li>
          <li>Routine post-operative follow-up care with Dr. Fitzer for one year</li>
        </ul>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          The hospital and anesthesia portions cover your one-day stay at INOVA Fair Oaks Hospital, the operating room, and anesthesia services during surgery.
        </p>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">What Is Not Included</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          These are billed separately, so plan for them as you budget:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6 text-foreground/80 leading-[1.6]">
          <li>
            <strong>Your initial consultation.</strong> But only if you decide not to proceed. In that case, we will bill your consult to insurance. Usually it is covered. If it is not covered for whatever reason, you will be charged $333.75. If you proceed with surgery, we will apply your consult fee payment to the surgery price.
          </li>
          <li>
            <strong>Psychological evaluation.</strong> Required before surgery. We can provide a referral list if you do not already have a provider.
          </li>
          <li>
            <strong>Medical clearance, labs, and EKG.</strong> These must be completed within 30 days of surgery and are typically billed to your insurance or your clearing physician.
          </li>
          <li>
            <strong>Additional testing.</strong> In some cases, your medical status calls for extra testing. Dr. Fitzer will tell you exactly what is and isn&rsquo;t needed at your first meeting. If you need a test, the office will be happy to help you set it up.
          </li>
          <li>
            <strong>Additional hospital days</strong> beyond the one day included cost $1,000 per day.
          </li>
          <li>
            <strong>Treatment of complications, revisions, or unrelated procedures.</strong>
          </li>
        </ul>

        <div className="mt-6 rounded-2xl border border-brand/30 bg-brand-soft/60 p-5 text-foreground/85 leading-[1.6]">
          <p>
            <strong>Note:</strong> if you begin the diet program and then decide not to proceed with surgery, each diet visit you completed will be billed at $85 per visit.
          </p>
        </div>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">How Payment Works</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          The surgeon fee is paid to our practice in two equal installments:
        </p>
        <ol className="mt-3 list-decimal space-y-1 pl-6 text-foreground/80 leading-[1.6]">
          <li>$3,900 due when you schedule your surgery date.</li>
          <li>$3,900 due at your final pre-operative appointment, three weeks before surgery.</li>
        </ol>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          Revision patients follow the same two-payment structure against the $10,000 revision surgeon fee. Hospital and anesthesia charges are billed separately by INOVA Fair Oaks Hospital and the anesthesia group &mdash; our office will give you their contact information so you can arrange payment directly with them. We accept cash, check, major credit cards, and HSA/FSA cards.
        </p>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">Financing and Payment Options</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          You do not have to pay for everything at once. Some patients use one of the following:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6 text-foreground/80 leading-[1.6]">
          <li>
            <strong>
              Explore{" "}
              <Link to="/resources/prosper" className="text-brand-mid hover:underline">
                financing and payment plans for weight-loss surgery
              </Link>.
            </strong>{" "}
            Prosper® offers fixed monthly payment plans for bariatric surgery. You apply directly with the lender and are usually approved within minutes.
          </li>
          <li>
            <strong>HSA and FSA funds.</strong> Bariatric surgery is generally an eligible medical expense, so pre-tax health savings dollars can be applied to your surgery. Confirm details with your plan administrator.
          </li>
          <li>
            <strong>Personal loan or line of credit.</strong> Some patients find a better rate through their own bank or credit union than through a medical lender. It is worth comparing.
          </li>
          <li>
            <strong>Employer benefits.</strong> Some employers offer weight-management or surgical benefits that sit outside your standard health plan. Ask your HR department before assuming you have no coverage.
          </li>
        </ul>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">Self-Pay Requirements</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          Self-pay removes insurance paperwork, but it does not remove the medical requirements that make surgery safe. Before your surgery date you will need:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-6 text-foreground/80 leading-[1.6]">
          <li>A BMI of 30 or higher.</li>
          <li>Completion of the 3-day diet program, which is included in your self-pay price.</li>
          <li>A psychological evaluation. Ask us for our referral list if you need one.</li>
          <li>Medical clearance, lab work, and an EKG completed within 30 days of your surgery date.</li>
          <li>Any additional testing Dr. Fitzer requires based on your medical history. He will tell you at your consultation exactly what applies to you.</li>
        </ul>
        <p className="mt-3 text-foreground/80 leading-[1.6]">
          Because self-pay patients are not waiting on insurance authorization, most people move from consultation to surgery considerably faster than the insurance pathway allows. If you are comparing paths, see our{" "}
          <Link to="/resources/qualifications" className="text-brand-mid font-medium hover:underline">bariatric surgery qualifications guide</Link>{" "}
          and{" "}
          <Link to="/resources/insurance-coverage" className="text-brand-mid font-medium hover:underline">verify your surgery benefit here</Link>.
        </p>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">Your Path to Surgery</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-6 text-foreground/80 leading-[1.6]">
          <li>
            Book a consultation. Call{" "}
            <a href="tel:7037099771" className="text-brand-mid font-medium hover:underline">(703) 709-9771</a>{" "}
            or{" "}
            <Link to="/appointments" className="text-brand-mid font-medium hover:underline">request an appointment online</Link>.
          </li>
          <li>Go to your consultation. Dr. Fitzer will review your history, discuss which procedure fits you, and outline any additional testing you need.</li>
          <li>Get a written quote. Our office confirms your self-pay total and walks you through the payment schedule and financing options.</li>
          <li>Complete your requirements. Psychological evaluation, medical clearance, labs, EKG, and the 3-day diet program.</li>
          <li>Schedule surgery. Your first $3,900 payment is due at scheduling.</li>
          <li>Final pre-op appointment. Three weeks before surgery, we confirm everything is in place and collect the second $3,900 payment.</li>
          <li>Surgery day at INOVA Fair Oaks Hospital. Get excited! Also, plan for a one-day hospital stay.</li>
          <li>Follow-up care. For the first year, your routine post-operative visits with Dr. Fitzer are included in your self-pay price.</li>
        </ol>

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">Self-pay bariatric surgery in Northern Virginia</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">All self-pay procedures are performed at INOVA Fair Oaks Hospital in Fairfax. Outpatient procedures such as ESG, TORe, the Orbera balloon and LAP-BAND usually mean home the same day, while sleeve and bypass include one hospital night. Patients traveling from out of the area can complete the first consultation by telehealth and plan a single trip to Herndon for surgery.</p>

        <ProcedureFaq heading="Frequently Asked Questions" faqs={FAQS} />

        <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">Request a Self-Pay Quote</h2>
        <p className="mt-3 text-center text-foreground/80 leading-[1.6]">
          Tell us how to reach you and which procedure you are considering. Our office will contact you to answer questions, confirm current pricing, and schedule a consultation with Dr. Fitzer.
        </p>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="mx-auto mt-6 max-w-2xl rounded-2xl border border-border bg-card p-4 text-left shadow-sm md:p-5"
          aria-label="Self-pay bariatric surgery inquiry form"
        >
          {sent ? (
            <div className="py-8 text-center">
              <h3 className="font-serif text-2xl text-brand-deep">Request received</h3>
              <p className="mt-2 text-muted-foreground leading-[1.6]">
                Thank you. Our office has received your self-pay inquiry and will be in touch shortly.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label htmlFor="self-pay-name" className="text-sm">
                  <span className="mb-1 block font-medium">Name<span aria-hidden="true" className="text-brand">*</span></span>
                  <input id="self-pay-name" required type="text" name="name" autoComplete="name" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
                </label>
                <label htmlFor="self-pay-phone" className="text-sm">
                  <span className="mb-1 block font-medium">Phone</span>
                  <input id="self-pay-phone" type="tel" name="phone" autoComplete="tel" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
                </label>
              </div>
              <label htmlFor="self-pay-email" className="block text-sm">
                <span className="mb-1 block font-medium">Email<span aria-hidden="true" className="text-brand">*</span></span>
                <input id="self-pay-email" required type="email" name="email" autoComplete="email" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
              </label>
              <label htmlFor="self-pay-procedure" className="block text-sm">
                <span className="mb-1 block font-medium">Procedure of interest</span>
                <select id="self-pay-procedure" name="procedure" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand">
                  <option value="">Not sure yet</option>
                  <option>Gastric Sleeve</option>
                  <option>Gastric Bypass</option>
                  <option>Lap-Band</option>
                  <option>Revision Surgery</option>
                  <option>Endoscopic Sleeve Gastroplasty (ESG)</option>
                  <option>TORe</option>
                  <option>Orbera Gastric Balloon</option>
                </select>
              </label>
              <label htmlFor="self-pay-message" className="block text-sm">
                <span className="mb-1 block font-medium">Questions or notes<span aria-hidden="true" className="text-brand">*</span></span>
                <textarea id="self-pay-message" required name="message" rows={4} maxLength={2000} className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
              </label>
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <div className="flex justify-center">
                <LazyTurnstile
                  siteKey={turnstileSiteKey}
                  triggerRef={formRef}
                  onSuccess={setTurnstileToken}
                  onError={() => setError("Security check failed. Please try again.")}
                  onExpire={() => setTurnstileToken(null)}
                />
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <button type="submit" disabled={sending || !turnstileToken} className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
                  {sending ? "Sending..." : "Request a self-pay quote"}
                </button>
                <a href="tel:7037099771" className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90">
                  Call (703) 709-9771
                </a>
              </div>
            </div>
          )}
        </form>
        <p className="mt-5 text-center text-sm text-muted-foreground leading-[1.6]">
          171 Elden Street, Suite 3B, Herndon, VA 20170 · Surgery performed at INOVA Fair Oaks Hospital, Fairfax, Virginia
        </p>
        <p className="mt-4 text-sm italic text-muted-foreground leading-[1.6]">
          Medical disclaimer: The information on this page is general in nature and is not medical advice. Pricing shown is an estimate of self-pay charges and is subject to change; your final cost depends on the procedure performed, your length of stay, and services billed by third parties including the hospital and anesthesia group. Candidacy for bariatric surgery is determined individually by Dr. Fitzer.
        </p>
      </PageSection>
    </>
  );
}
